import React, { useEffect, useState } from "react";
import { CheckCircle2, Inbox, Mail, RefreshCw, RotateCcw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const FILTERS = [
  { value: "new", label: "New" },
  { value: "handled", label: "Handled" },
  { value: "all", label: "All" },
];

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-[#E3DED4] bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#7A868B]">{label}</p>
      <p className="font-display text-3xl font-bold text-[#1F2A2E] mt-2">{value}</p>
    </div>
  );
}

export default function ContactInbox() {
  const [filter, setFilter] = useState("new");
  const [data, setData] = useState({ messages: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/contact-messages?status_filter=${f}`);
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const resolve = async (id, status) => {
    setBusy(id);
    try {
      await api.post(`/admin/contact-messages/${id}/resolve`, { status });
      toast.success(status === "handled" ? "Marked as handled." : "Moved back to new.");
      await load(filter);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const c = data.counts || {};

  return (
    <div className="mt-6 space-y-5" data-testid="admin-contact-inbox">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-[#1F2A2E]">Contact inbox</h2>
          <p className="text-sm text-[#4B5A60]">
            Messages sent from the public contact page - no account required to send one.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl border-2 border-[#E3DED4] bg-white overflow-hidden">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                data-testid={`contact-filter-${f.value}`}
                className={`px-3 py-2 text-sm font-semibold transition-colors duration-150 ${
                  filter === f.value
                    ? "bg-[#F2FAFA] text-[#1F6F78]"
                    : "text-[#4B5A60] hover:bg-[#F7F4EF]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => load(filter)}
            data-testid="contact-inbox-refresh-btn"
            className="border-[#E3DED4] text-[#1F6F78]"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testId="contact-stat-new" label="New" value={c.new ?? 0} />
        <Stat testId="contact-stat-safety" label="New safety reports" value={c.safety_new ?? 0} />
        <Stat testId="contact-stat-handled" label="Handled" value={c.handled ?? 0} />
        <Stat testId="contact-stat-total" label="All time" value={c.total ?? 0} />
      </div>

      {data.messages.length === 0 ? (
        <div
          data-testid="contact-inbox-empty"
          className="rounded-2xl border border-dashed border-[#E3DED4] bg-[#FBF9F5] p-10 text-center"
        >
          <Inbox className="w-6 h-6 text-[#B9C2C0] mx-auto" />
          <p className="mt-2 text-sm font-semibold text-[#4B5A60]">
            {filter === "new" ? "No new messages" : "Nothing here yet"}
          </p>
          <p className="text-xs text-[#7A868B] mt-1">
            Messages from the contact page land here within seconds.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="contact-inbox-list">
          {data.messages.map((m) => (
            <article
              key={m.id}
              data-testid={`contact-message-${m.id}`}
              className="rounded-2xl border border-[#E3DED4] bg-white p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-display text-lg font-bold text-[#1F2A2E]">{m.name}</h3>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        m.topic === "safety"
                          ? "bg-[#FDF0E7] text-[#8A4B22]"
                          : "bg-[#E9F3F3] text-[#1F6F78]"
                      }`}
                    >
                      {m.topic === "safety" && (
                        <ShieldAlert className="w-3 h-3 inline mr-1 -mt-0.5" />
                      )}
                      {m.topic_label}
                    </span>
                    {m.status === "handled" && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#E4F3EA] text-[#14603C]">
                        Handled
                      </span>
                    )}
                  </div>
                  <a
                    href={`mailto:${m.email}`}
                    className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F6F78] hover:underline"
                    data-testid={`contact-reply-${m.id}`}
                  >
                    <Mail className="w-3.5 h-3.5" />
                    {m.email}
                  </a>
                  {m.about_username && (
                    <p className="mt-1 text-xs text-[#7A868B]">
                      About member: <strong className="text-[#4B5A60]">{m.about_username}</strong>
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-[#7A868B]">
                    {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                  </p>
                  <Button
                    variant="outline"
                    disabled={busy === m.id}
                    onClick={() => resolve(m.id, m.status === "handled" ? "new" : "handled")}
                    data-testid={`contact-resolve-${m.id}`}
                    className="mt-2 border-[#E3DED4] text-[#1F6F78]"
                  >
                    {m.status === "handled" ? (
                      <>
                        <RotateCcw className="w-4 h-4 mr-1.5" />
                        Reopen
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-1.5" />
                        Mark handled
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <p className="mt-3 whitespace-pre-wrap text-[#4B5A60] leading-7">{m.message}</p>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
